import React from 'react';
import { EpicGamesToggle, PropertyLogo, SiteNav, RightNav, DividerLine } from './components';
import { NavStore } from './stores';
import debounce from 'lodash/debounce';
import clone from 'lodash/clone';
import each from 'lodash/each';
import { defaultMessages, properties } from './constants';
import { getPropertyObject, getPropertyName } from './utils/epicPropertyInspector';
import connectToStores from 'alt-utils/lib/connectToStores';
import { setDataAttribute } from './utils/compatibilityUtils';
import { updateDimensions, getWindowWidth, getWindowHeight } from './utils/resizeUtils';
import browser from 'bowser';

//no sass include since its taken care of in another bundle

const versionJson = require('../../version.json');

@connectToStores
export default class EpicGamesNav extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isMobile: false, //is mobile mode with hamburger on top right
            isTablet: false, //is tablet mode with hamburger
            isDesktop: true, // is desktop mode
            collapsedDesktop: false, // cant fit the links in desktop mode
            resizing: false, //currently performing a resize
            init: false
        };

        this.warnedSearch = false;

        this.userAgentSet = false;

        this.lastWidth = null;
        this.lastHeight = null;

        this.localeStringOnBody = '';
        this.cache = {};
        this.dimensionsInitialized = false;
        this.needWidthReCalc = false;

        this.updateDimensionsDebounce = debounce(updateDimensions, 25);

        this.updateDimensionsOnResize = () => {
            const height = getWindowHeight();
            const width = getWindowWidth();
            if (height === this.lastHeight && width === this.lastWidth) {
                if (process.env.NODE_ENV !== 'production') {
                    console.debug('[EGH] resize event skipped. No window size change detected');
                }
                return; //browser window didn't actually resize
            }
            this.lastWidth = width;
            this.lastHeight = height;

            this.calcDimensions();
            if (this.props.navStore.searchActive && this.state.isMobile) return; //keyboard opened on mobile... skip state resizing
        };
    }

    static getStores() {
        return [NavStore];
    }

    static getPropsFromStores() {
        return {
            navStore: NavStore.getState()
        };
    }

    static childContextTypes = {
        isMobile: React.PropTypes.bool,
        isTablet: React.PropTypes.bool,
        collapsedDesktop: React.PropTypes.bool,
        isDesktop: React.PropTypes.bool,
        messages: React.PropTypes.object
    }

    componentDidMount() {
        setTimeout(() => {
            this.setLocaleClassOnBody(this.props);
            this.setUserAgent();
        }, 1);

        window.addEventListener('resize', this.updateDimensionsOnResize);

        //allow external sites to apply logic when the header is rendered
        if (typeof window._egNavReadyCallback === 'function') {
            window._egNavReadyCallback();
            window._egNavReadyCallbackRun = true;
        }
        window._egNavReadyCallbackCalled = true;

        //Give some time for the css to be applied otherwise the measurements could be off
        setTimeout(() => {
            this.dimensionsInitialized = true;
            this.calcDimensions();
        }, 10);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateDimensionsOnResize);
    }

    componentWillReceiveProps(nextProps) {
        if (!this.localeStringOnBody || this.props.locale !== nextProps.locale) {
            this.setLocaleClassOnBody(nextProps);
        }

        if (this.dimensionsInitialized &&
            (this.props.links.length !== nextProps.links.length ||
            this.props.epicPropertyName !== nextProps.epicPropertyName)) {
            //may need to check sizes again
            this.needWidthReCalc = true;
        }
    }

    componentWillUpdate(nextProps, nextState) {
        if (!this.state.init && nextState.init) {
            //may need to check sizes again
            this.needWidthReCalc = true;
        }
    }

    componentDidUpdate() {
        if (this.needWidthReCalc) {
            this.calcDimensions();
        }
    }

    getChildContext() {
        return {
            isMobile: this.state.isMobile,
            isTablet: this.state.isTablet,
            isDesktop: this.state.isDesktop,
            collapsedDesktop: this.state.collapsedDesktop,
            messages: this.getMessages()
        };
    }

    calcDimensions() {
        if (!this.state || this.state.resizing) return;
        if (!this.resizing) this.setState({ resizing: true });

        const end = () => {
            this.setState({ resizing: false });
        };

        this.updateDimensionsDebounce(this.state, ({ isMobile, isTablet, isDesktop, collapsedDesktop, needWidthReCalc }) => {
            this.setState({
                isMobile,
                isTablet,
                isDesktop,
                collapsedDesktop,
                init: true
            });
            this.needWidthReCalc = needWidthReCalc;
            end();
        }, () => {
            if (!this.state.init) {
                this.setState({ init: true });
            }
            end();
        });
        this.needWidthReCalc = false;
    }

    setUserAgent() {
        const body = document.body;
        if (!this.userAgentSet) {
            this.userAgentSet = true;

            for (const key in browser) {
                if (typeof key === 'string' && typeof browser[key] !== 'function' && key.length > 1 &&
                    key !== 'name' && key !== 'version') {
                    setDataAttribute(body, key, true);
                }
            }

            setDataAttribute(body, 'browserVersion', browser.version);

            if (!window._epicGamesNav) {
                window._epicGamesNav = {};
            }
            window._epicGamesNav.browser = browser;
        }
    }

    setLocaleClassOnBody(props) {
        const locale = props.locale || '';
        const escapedLocale = locale.replace(/\-/, '\\-');
        const regExString = `/${escapedLocale}/`;
        const reg = new RegExp(regExString);
        if (typeof document.body !== 'undefined' && typeof document.body.className !== 'undefined' && !reg.test(document.body.className)) {
            if (this.localeStringOnBody && locale !== this.localeStringOnBody) {
                //remove the previous locale
                document.body.className = document.body.className.replace(this.localeStringOnBody, '').replace(/[\s]+/g, ' ');
                this.localeStringOnBody = locale;
                if (document.body.className.length) {
                    document.body.className += ` ${locale}`;
                } else {
                    document.body.className += locale;
                }
            } else if (this.localeStringOnBody && locale === this.localeStringOnBody) {
                //current string is fine
            } else {
                this.localeStringOnBody = locale;
                if (document.body.className.length) {
                    document.body.className += ` ${locale}`;
                } else {
                    document.body.className += locale;
                }
            }
        }
    }

    /**
     * Merges the default messages with the props passed into the component if any. Will cache the result to make
     * future renders much faster rather than merging every time.
     *
     * Checks the props for valid message constants otherwise returns the default values
     */
    getMessages() {
        let m = {};

        //check cache to see if props have not changed and skip the iteration
        if (this.props.messages && this.props.messages.asMutable && this.props.messages === this.cache.messages) {
            m = this.cache.m;
        } else {
            if (this.props.messages) {
                for (const key in defaultMessages) {
                    if (typeof key === 'string' && this.props.messages[key] && this.props.messages[key].length) {
                        m[key] = this.props.messages[key];
                    } else {
                        m[key] = defaultMessages[key];
                    }
                }
            } else {
                m = defaultMessages;
            }

            this.cache.messages = this.props.messages;
            this.cache.m = m;
        }
        return m;
    }

    /**
     * Merges the default properties with the props passed into the component if any. Will cache the result to make
     * future renders much faster rather than merging every time.
     *
     * NOTE: properties refers to the epic games properties to be listed under the epic shield
     * @return {array} Array of epic properties for the sheild
     */
    getProperties() {
        let p = [];
        if (this.props.properties && this.props.properties.asMutable && this.props.properties === this.cache.properties) {
            p = this.cache.p;
        } else {
            for (const key in properties) {
                if (typeof key === 'string') {
                    const egDefaultProp = properties[key];
                    egDefaultProp.propertyKey = key;
                    let egProp;

                    if (this.props.properties) {
                        each(this.props.properties, (prop) => {
                            if (prop.propertyKey === key) {
                                egProp = prop;
                                return false;
                            }
                            return undefined;
                        });
                    }
                    if (egProp) {
                        const mergedProp = clone(egDefaultProp);
                        //check inside of the object
                        for (const field in egProp) {
                            if (typeof field === 'string') {
                                if (typeof egProp[field] !== 'undefined') {
                                    mergedProp[field] = egProp[field];
                                } else {
                                    mergedProp[field] = egDefaultProp[field];
                                }
                            }
                        }
                        p.push(mergedProp);
                    } else {
                        p.push(egDefaultProp);
                    }
                }
            }
            this.cache.properties = this.props.properties;
            this.cache.p = p;
        }
        return p;
    }

    render() {
        const epicPropertyName = getPropertyName(this.props.epicPropertyName);
        const epicPropertyConfig = getPropertyObject(epicPropertyName);
        const headerClassName = epicPropertyConfig.headerClassName;
        const epicPropertyClasses = this.props.epicPropertyClasses || '';
        const links = this.props.links;
        const epicProperties = this.getProperties();
        if (!epicPropertyConfig || !links) {
            console.error('[EGH] Failed to initialize properly! Missing a valid epicPropertyConfig or links key!');
        }

        let horizontalSiteNav;
        if (!this.state.isMobile) {
            horizontalSiteNav = (
                <SiteNav
                    links={links}
                    showLinks={this.props.navStore.tabletNavActive}
                    mobileActiveLinkIndex={this.props.navStore.mobileActiveLinkIndex}
                    searchActive={this.props.navStore.searchActive}/>
            );
        }

        const menuOpen = this.props.navStore.mobileNavActive || this.props.navStore.tabletNavActive || this.props.navStore.egNavActive ? 'menu-open' : '';

        const navResponsiveClasses = `${this.state.isTablet ? 'tablet' : ''} ${this.state.isDesktop ?
            'desktop' : ''} ${this.state.isMobile ? 'mobile' : ''}`;

        let hideSearch = this.props.hideSearch;
        if (!hideSearch && !this.props.onSearch) {
            hideSearch = true;
            if (!this.warnedSearch) {
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('[EGH] hideSearch is false but no onSearch callback is specified. Search capabilities cannot be displayed.');
                }
                this.warnedSearch = true;
            }
        }

        return (
            <header
                id="egh"
                className={`${headerClassName} ${epicPropertyClasses} ${this.state.resizing ? 'resizing' : ''} ${this.state.init ? 'init' : ''}`}
                data-version={versionJson.version || ''}>

                <nav
                    id="nav"
                    className={`${menuOpen} ${navResponsiveClasses}`}>
                    <div>
                        <div
                            id="propNav">
                            <EpicGamesToggle
                                isActive={this.props.navStore.egNavActive}
                                epicPropertyName={epicPropertyName}
                                epicPropertyConfig={epicPropertyConfig}
                                properties={epicProperties}
                                downloadLink={this.props.downloadLink}
                                pcDownloadUrl={this.props.pcDownloadUrl}
                                macDownloadUrl={this.props.macDownloadUrl}
                                searchActive={this.props.navStore.searchActive}/>

                            <PropertyLogo
                                logoLink={this.props.logoLink}
                                searchActive={this.props.navStore.searchActive}/>

                            <DividerLine
                                isActive={this.props.navStore.tabletNavActive}
                                searchActive={this.props.navStore.searchActive}/>

                            {horizontalSiteNav}
                        </div>
                        <RightNav
                            searchActive={this.props.navStore.searchActive}
                            isLoggedIn={this.props.isLoggedIn}
                            hideLocale={this.props.hideLocale}
                            hideSearch={hideSearch}
                            hideSignIn={this.props.hideSignIn}
                            hideCallToAction={this.props.hideCallToAction}
                            callToAction={this.props.callToAction}
                            links={links}
                            isActive={this.props.navStore.mobileNavActive}
                            localeOptions={this.props.localeOptions}
                            showLinks={this.props.navStore.mobileNavActiveLinks}
                            mobileUserActive={this.props.navStore.mobileUserActive}
                            mobileActiveLinkIndex={this.props.navStore.mobileActiveLinkIndex}
                            mobileLocaleActive={this.props.navStore.mobileLocaleActive}
                            locale={this.props.locale}
                            displayName={this.props.displayName}
                            accountLinks={this.props.accountLinks}
                            onSearch={this.props.onSearch}
                            maxWidth={this.maxRightNavWidth}/>
                    </div>
                </nav>
            </header>
        );
    }
}

EpicGamesNav.propTypes = {
    epicPropertyName: React.PropTypes.string,
    epicPropertyClasses: React.PropTypes.string,
    navStore: React.PropTypes.object,
    isLoggedIn: React.PropTypes.bool,
    links: React.PropTypes.array,
    hideLocale: React.PropTypes.bool,
    hideSearch: React.PropTypes.bool,
    hideSignIn: React.PropTypes.bool,
    downloadLink: React.PropTypes.object,
    hideCallToAction: React.PropTypes.bool,
    callToAction: React.PropTypes.object,
    localeOptions: React.PropTypes.array,
    logoLink: React.PropTypes.object,
    displayName: React.PropTypes.string,
    locale: React.PropTypes.string,
    accountLinks: React.PropTypes.object,
    messages: React.PropTypes.object,
    properties: React.PropTypes.array,
    onSearch: React.PropTypes.func,
    pcDownloadUrl: React.PropTypes.string,
    macDownloadUrl: React.PropTypes.string
};



/** WEBPACK FOOTER **
 ** ./app/scripts/EpicGamesNav.js
 **/